import React, { useState } from 'react';
import { Box, IconButton, Popover, Tooltip, Typography } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import type { PartFeedback } from '../../utils/workingStepFeedback.ts';

type Props = {
  feedback: PartFeedback;
  label: string;
};

const StepFeedbackMarker: React.FC<Props> = ({ feedback, label }) => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const { status, comment } = feedback;

  const Icon =
    status === 'correct' ? CheckCircleIcon : status === 'wrong' ? CancelIcon : HelpOutlineIcon;
  const color =
    status === 'correct'
      ? 'success.main'
      : status === 'wrong'
        ? 'error.main'
        : 'warning.main';
  const statusLabel =
    status === 'correct' ? 'Correct' : status === 'wrong' ? 'Incorrect' : 'Not matched';
  const hasPopover = status !== 'correct' && Boolean(comment?.trim());

  const icon = (
    <IconButton
      size="small"
      aria-label={`${label}: ${statusLabel}`}
      onClick={hasPopover ? (event) => setAnchor(event.currentTarget) : undefined}
      sx={{
        color,
        p: 0.35,
        mt: 0.35,
        cursor: hasPopover ? 'pointer' : 'default',
      }}
      disableRipple={!hasPopover}
    >
      <Icon sx={{ fontSize: 20 }} />
    </IconButton>
  );

  return (
    <>
      {status === 'correct' ? (
        <Tooltip title="Correct" arrow>
          {icon}
        </Tooltip>
      ) : status === 'neutral' ? (
        <Tooltip title={comment?.trim() || 'Not matched'} arrow>
          {icon}
        </Tooltip>
      ) : (
        icon
      )}

      {hasPopover && (
        <Popover
          open={Boolean(anchor)}
          anchorEl={anchor}
          onClose={() => setAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        >
          <Box sx={{ p: 1.5, maxWidth: 300 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              {label} — {statusLabel}
            </Typography>
            <Typography variant="body2">{comment}</Typography>
          </Box>
        </Popover>
      )}
    </>
  );
};

export default StepFeedbackMarker;
